import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import BusyButton from 'scoreboard/components/busy-button'
import template from './strong-confirm-prompt.hbs'

/**
 * @class DeleteConfirmation
 */
var StrongConfirmPrompt = Backbone.View.extend({
	tagName: 'form',
	className: 'strong-confirm-prompt',

	events: {
		'submit': '_confirm',
		'click .cancel': 'close',
		'input input': '_onInput'
	},

	/**
	 * A custom confirmation prompt, requiring the user to type in a particular phrase
	 * to continue. Defaults are appropriate for deleting organizations, but options
	 * can be provided to also confirm things like creating a large number of org copies.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {string} options.message
	 * caller is responsible for any formatting.
	 *
	 * @param {string} options.label
	 * The organization label for which to confirm deletion, will be displayed just
	 * before the input field where the user has to enter "delete"
	 *
	 * @param {string} [options.dialogTitle='common.delete.confirm.title']
	 * Title for the dialog
	 *
	 * @param {string} [options.prompt='common.delete.confirmation.prompt']
	 * Text for the "Type delete to..." prompt.  If this is not provided then options.list
	 * will be used to dictate the prompt text that is displayed.
	 *
	 * @param {string} [options.confirmationText='common.delete']
	 * Text the user needs to type in the input box
	 *
	 * @param {string} [options.confirmationButton='common.delete']
	 * Label for the button that triggers "confirm"
	 *
	 * @param {boolean} [options.showUndoneWarning=true]
	 * If true, will display "Caution: this action can't be undone" message
	 *
	 * @param {boolean} [options.wrapMessageInParagraph=true] if true, wraps
	 * `message` in a paragraph tag.
	 *
	 * @fires confirm - when the user has passed all delete confirmation challenges
	 * and the resource should be physically deleted.
	 *
	 * @fires cancel - when the user cancels without confirming the deletion
	 */
	initialize: function(options) {
		this.options = options || {}
		this.wrapMessageInParagraph = options.wrapMessageInParagraph ?? true
		this.requiredConfirmationText = _.lowerCase(options.confirmationText || i18n.get('common.delete'))
		this.showUndoneWarning = _.isNil(options.showUndoneWarning) ? true : options.showUndoneWarning
	},

	render: function() {
		var message = this.options.message
		if (!_.isNil(message) && this.wrapMessageInParagraph) {
			message = `<p>${message}</p>`
		}

		var confirmMsgKey = _.isEmpty(this.options.list)
			? 'common.delete.confirmation.prompt'
			: 'common.delete.confirmation.prompt.withDescendants'

		var context = {
			message: message,
			confirmPrompt: this.options.prompt || i18n.get(confirmMsgKey, _.escape(this.options.label)),
			dialogTitle: this.options.dialogTitle || i18n.get('common.delete.confirm.title'),
			showUndoneWarning: this.showUndoneWarning,
			confirmationButton: this.options.confirmationButton || i18n.get('common.delete')
		}
		this.$el.append(template(context))
		this._checkConfirmationText()
		return this
	},

	// handles clicking close or pressing escape
	close: function() {
		this.trigger('cancel')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.busyButton], 'remove')
	},

	_confirm: function(e) {
		e.preventDefault()
		// #20834 make sure button is enabled before submitting form
		// since Safari/IE can submit form with Enter key before typing "delete"
		if (this.$('.button-danger').prop('disabled')) {
			e.stopPropagation()
			return
		}

		this.busyButton = new BusyButton({ el: this.$('.button-danger') }).loading()
		this.trigger('confirm')
	},

	_onInput: function(e) {
		this._checkConfirmationText()
	},

	_checkConfirmationText: function() {
		var confirmText = this.$('.confirmation').val()
		var matches = this.requiredConfirmationText === _.lowerCase(_.trim(confirmText))
		this.$('.button-danger').prop('disabled', !matches)
	}

})

export default StrongConfirmPrompt

StrongConfirmPrompt.__filename__ = 'components/strong-confirm-prompt.js'
